import { Affiliate } from '../../models/affiliate';
import { AvailabilityType } from './useFetchAvailability';
import {
  CareVariant,
  DEFAULT_COST_ANSWER,
  STRIPE_COST_ANSWER,
  VISIT_COPY,
} from './schedulerCopy';

export enum CostAnswerKind {
  hidden = 'hidden',
  affiliate = 'affiliate',
  stripe = 'stripe',
  default = 'default',
}

export type CostAnswer =
  | { kind: CostAnswerKind.hidden }
  | { kind: CostAnswerKind.affiliate; body: string }
  | { kind: CostAnswerKind.stripe; lead: string; paragraphs: string[] }
  | { kind: CostAnswerKind.default; paragraphs: string[] };

/**
 * Which cost answer to show, following the prototype's rule.
 *
 * There, the generic estimate appears only when the affiliate has no band
 * header and an affiliate's own copy appears only when it has one.
 * An affiliate with a header but no body therefore shows nothing,
 * which is why the fully covered ones (PDS, JDRC, Cleveland Clinic)
 * hide the question rather than following "no cost to you" with a price range.
 */
export const getCostAnswer = ({
  affiliate,
  isStripe,
}: {
  affiliate?: Affiliate;
  isStripe?: boolean;
}): CostAnswer => {
  if (isStripe) {
    return { kind: CostAnswerKind.stripe, ...STRIPE_COST_ANSWER };
  }

  const body = affiliate?.cost_transparency_body?.trim();
  if (body) {
    return { kind: CostAnswerKind.affiliate, body };
  }

  if (!affiliate?.cost_transparency_header?.trim()) {
    return { kind: CostAnswerKind.default, ...DEFAULT_COST_ANSWER };
  }

  return { kind: CostAnswerKind.hidden };
};

/**
 * The care variant for the copy. Driven by the user's own assignment rather
 * than the affiliate's advertised model, so a coach-led user under an
 * affiliate that offers both still reads the coach wording.
 */
export const getCareVariant = (
  availabilityType: AvailabilityType
): CareVariant =>
  availabilityType === AvailabilityType.coach ? 'coach' : 'clinic';

export const getVisitCopy = (variant: CareVariant) => VISIT_COPY[variant];
